import React, { useEffect, useMemo, useRef } from 'react';
import { useLocalParticipant } from '@livekit/components-react';

/**
 * Always-on local camera preview so you can see yourself even if the
 * conference grid has not laid out a tile yet.
 */
const CallLocalPreview = () => {
  const {
    localParticipant,
    isCameraEnabled,
    isMicrophoneEnabled,
    cameraTrack,
    lastCameraError,
  } = useLocalParticipant();
  const videoRef = useRef(null);
  const mediaTrack = cameraTrack?.track;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !mediaTrack) return undefined;
    mediaTrack.attach(el);
    const play = () => {
      el.play().catch(() => {});
    };
    play();
    el.addEventListener('loadedmetadata', play);
    return () => {
      el.removeEventListener('loadedmetadata', play);
      try {
        mediaTrack.detach(el);
      } catch {
        // ignore
      }
      el.srcObject = null;
    };
  }, [mediaTrack]);

  const label = useMemo(() => {
    const name = localParticipant?.name || localParticipant?.identity || 'You';
    return name === localParticipant?.identity ? 'You' : `${name} (you)`;
  }, [localParticipant]);

  if (!localParticipant) return null;

  return (
    <div className="pointer-events-none absolute bottom-3 right-3 z-20 w-44 sm:w-52 aspect-video rounded-xl overflow-hidden border border-white/20 bg-black shadow-xl">
      <video
        ref={videoRef}
        className={`h-full w-full object-cover ${isCameraEnabled && mediaTrack ? '' : 'hidden'}`}
        style={{ transform: 'rotateY(180deg)' }}
        muted
        playsInline
        autoPlay
      />
      {!(isCameraEnabled && mediaTrack) && (
        <div className="absolute inset-0 flex items-center justify-center px-2 text-center text-[11px] text-white/70">
          {lastCameraError?.message || (isCameraEnabled ? 'Starting camera…' : 'Camera off')}
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 px-1.5 py-1 bg-black/65 text-[10px] font-medium">
        <span className="truncate">{label}</span>
        <span className={isMicrophoneEnabled ? 'text-emerald-300' : 'text-red-300'}>
          {isMicrophoneEnabled ? 'Mic on' : 'Mic off'}
        </span>
      </div>
    </div>
  );
};

export default CallLocalPreview;
